import { createApi, BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { AxiosRequestConfig, AxiosError } from 'axios';
import axiosInstance from 'api/axios';

const axiosBaseQuery =
  (): BaseQueryFn<
    {
      url: string;
      method?: AxiosRequestConfig['method'];
      data?: AxiosRequestConfig['data'];
      params?: AxiosRequestConfig['params'];
    },
    unknown,
    unknown
  > =>
  async ({ url, method = 'GET', data, params }) => {
    try {
      const result = await axiosInstance({ url, method, data, params });
      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError;
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

export const paymentApi = createApi({
  reducerPath: 'paymentApi',
  baseQuery: axiosBaseQuery(),
  keepUnusedDataFor: 1800, // 30 minutes caching
  tagTypes: ['DepositMethods'],
  endpoints: (builder) => ({
    getDepositMethods: builder.query<any, void>({
      query: () => ({
        url: '/client/payment/id/deposit-methods?isFavorite=true',
        method: 'GET',
      }),
      providesTags: ['DepositMethods'],
    }),
    getWithdrawMethods: builder.query<any, void>({
      query: () => ({
        url: '/client/payment/id/withdraw-methods?isFavorite=true',
        method: 'GET',
      }),
    }),
    toggleNetworkFavorite: builder.mutation<
      { networkId: number; isFavorite: boolean },
      { id: number; isFavorite: boolean }
    >({
      query: ({ id, isFavorite }) => ({
        url: `/client/payment/networks/${id}/favorite`,
        method: isFavorite ? 'PUT' : 'DELETE',
      }),
      invalidatesTags: ['DepositMethods'],
    }),
    initiateTip: builder.mutation<
      any,
      { receiverUsername: string; amount: string; currencyCode: string }
    >({
      query: (body) => ({
        url: '/client/balance/tip',
        method: 'POST',
        data: body,
      }),
    }),
    confirmTip: builder.mutation<any, { code: string }>({
      query: (body) => ({
        url: '/client/balance/tip/confirm',
        method: 'POST',
        data: body,
      }),
    }),
  }),
});

export const {
  useGetDepositMethodsQuery,
  useGetWithdrawMethodsQuery,
  useToggleNetworkFavoriteMutation,
  useInitiateTipMutation,
  useConfirmTipMutation,
} = paymentApi;
